import { useState, useCallback, useRef } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useSubmitOrder } from '@zenbusiness/application-commons-ecommerce';
import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { useNotification } from '#client/App/NotificationManager';
import useFlowState from '#client/hooks/flow/useFlowState';
import { useOrderStatusMonitor } from '#client/hooks/useOrderStatusMonitor';
import { focusFirstErrorField } from '#client/utils/form';
import { formErrorsToTrackingErrorsMapper } from '#client/utils/formErrorsToTrackingErrorsMapper';

import { useCheckout } from './useCheckout';
import { usePaymentContext } from './usePaymentContext';
import { usePlaceOrder } from './usePlaceOrder';
import { PAYMENT_ACTIONS } from '../constants/payment';
import { SECTION_KEYS } from '../constants/sections';

// Note: These are hardcoded values for the Smart LLC formation test.
const ENTITY_TYPE = 'llc';
const JURISDICTION_STATE = 'WY';

export function useCheckoutSubmission({ onOrderSuccess, sections }) {
  const captureException = useCaptureException();
  const { completeSection, invalidateSection, formMethods } = useCheckout();
  const { set } = useFlowState();
  const { showToast } = useNotification();
  const [, dispatch] = usePaymentContext();
  const [orderUuid, setOrderUuid] = useState(null);
  const [paymentMethodType, setPaymentMethodType] = useState(null);
  const { errors: orderErrors, status: orderStatus, submitOrder } = useSubmitOrder();

  const trackFormSubmitted = useClickstreamCallback('formSubmitted');
  const trackFormSubmissionFailed = useClickstreamCallback('formSubmissionFailed');

  // Use a ref to store validated form data between validation and order processing
  const validatedFormDataRef = useRef(null);

  /**
   * Order Processing
   */

  const handleOrderError = useCallback(
    (orderError) => {
      dispatch({
        type: PAYMENT_ACTIONS.PLACE_ORDER_ERROR,
        payload: orderError
      });
    },
    [dispatch]
  );

  const placeOrder = usePlaceOrder({
    onError: handleOrderError,
    submitOrder
  });

  // Function to process the order and update the payment and order state.
  const processOrder = useAsyncCallback(
    async (paymentData) => {
      const { cardToken, paymentMethodType: pmt } = paymentData || {};

      // Get the current values from storage and fallback to the form values.
      const formValues = validatedFormDataRef.current || formMethods.getValues();
      const {
        accountInfo: accountInfoData,
        businessInfo: businessInfoData,
        contactInfo: contactInfoData
      } = formValues || {};

      // Extract business info from form values
      const businessInfo = {
        name: businessInfoData?.name,
        companyIndicator: businessInfoData?.companyIndicator,
        entityType: ENTITY_TYPE,
        jurisdictionState: JURISDICTION_STATE
      };

      // Extract account info from form values
      const contactInfo = {
        firstName: contactInfoData?.firstName,
        lastName: contactInfoData?.lastName,
        phone: contactInfoData?.phone
      };

      // Get password from form values
      const accountInfo = {
        ...contactInfo, // firstName, lastName, phone
        ...accountInfoData // email, password
      };

      // Kick off payment place order state tracking
      dispatch({ type: PAYMENT_ACTIONS.PLACE_ORDER_START });

      // Begin the order process
      const processedOrderUuid = await placeOrder({
        accountInfo,
        businessInfo,
        cardToken
      });

      // Update the order state if successful
      if (processedOrderUuid) {
        setOrderUuid(processedOrderUuid);
        setPaymentMethodType(pmt);
      }
    },
    [placeOrder, dispatch, formMethods]
  );

  /**
   * Order Monitoring
   */

  const handleOrderMonitorFailure = useCallback(
    (orderError) => {
      const { message, extensions } = orderError;
      dispatch({
        type: PAYMENT_ACTIONS.PLACE_ORDER_ERROR,
        payload: { message, ...extensions }
      });
    },
    [dispatch]
  );

  const handleOrderMonitorSuccess = useCallback(() => {
    set('orderUuid', orderUuid);
    dispatch({ type: PAYMENT_ACTIONS.PLACE_ORDER_SUCCESS });
    onOrderSuccess?.();
  }, [dispatch, onOrderSuccess, orderUuid, set]);

  useOrderStatusMonitor({
    orderErrors,
    orderStatus,
    orderUuid,
    paymentMethodType,
    onFailure: handleOrderMonitorFailure,
    onSuccess: handleOrderMonitorSuccess
  });

  /**
   * Order form validation
   */

  const handleValidationSuccess = useCallback(() => {
    trackFormSubmitted({
      elementName: 'checkout_smart_llc_form'
    });

    // Mark sections as complete
    sections.forEach((sectionKey) => {
      if (sectionKey !== SECTION_KEYS.PAYMENT_INFO) {
        completeSection(sectionKey);
      }
    });
  }, [completeSection, sections, trackFormSubmitted]);

  const handleValidationFailure = useCallback(
    (validationErrors) => {
      trackFormSubmissionFailed({
        formErrors: formErrorsToTrackingErrorsMapper(validationErrors)
      });

      showToast({
        // TODO: Add Intl message strings.
        title: 'Form Validation Error',
        subtitle: 'Please check for errors and try again.',
        variant: 'alert'
      });

      focusFirstErrorField(validationErrors, formMethods);

      // Update validation status for each section
      const sectionsWithErrors = Object.keys(validationErrors);
      sections.forEach((sectionKey) => {
        if (sectionsWithErrors.includes(sectionKey)) {
          invalidateSection(sectionKey);
        } else {
          completeSection(sectionKey);
        }
      });
    },
    [
      completeSection,
      formMethods,
      invalidateSection,
      sections,
      showToast,
      trackFormSubmissionFailed
    ]
  );

  const validateOrderForm = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Get current form values regardless of validation outcome
      validatedFormDataRef.current = formMethods.getValues();

      set('formationInfo', {
        businessName: validatedFormDataRef.current.businessInfo.name,
        principalAddress: validatedFormDataRef.current.principalAddress,
        mailingAddress: validatedFormDataRef.current.mailingAddress,
        owner: validatedFormDataRef.current.contactInfo
      });

      formMethods
        .handleSubmit(
          (validData) => {
            handleValidationSuccess(validData);
            resolve(true);
          },
          (errors) => {
            handleValidationFailure(errors);
            reject(errors);
          }
        )()
        .catch((error) =>
          captureException(error, {
            level: 'info'
          })
        );
    });
  }, [captureException, formMethods, handleValidationFailure, handleValidationSuccess, set]);

  return {
    orderUuid,
    processOrder,
    validateOrderForm
  };
}
